import { useSelector } from 'react-redux'
import ChartWrapper from '../../../chartV2/chartWrapper/chartWrapper'
import { DashboardSyncEvents, DashboardSyncSettings, IWidget, WidgetProps, WidgetSettings } from '../../widgetDashboard.interface'
import { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { selectChartComparableSecurities,selectChartTimeBucket, selectTimeBuckets } from '../../../../selectors/chart'
import { useDispatch } from 'react-redux'
import { getIsWidgetSlotActiveSelector } from '../../../../selectors/widgetDashboard'
import { deferAfterPaneSelectionPaint } from '../../deferPaneSideEffects'
import store from '../../../../store'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import { deleteChartConfig, setActiveChart, updateChartConfig, updateChartDrawings, updateChartTimeBucket } from '../../../../reducer/chart.reducer'
import { updateWidgetSettings } from '../../../../reducer/widgetDashboard.reducer'
import { AppContext } from '../../../../App'
import { CrossHairInfo, ViewportCandleInfo,getDefaultChartConfig } from '../../../../util/chart'
import { TimeDurations } from '../../../screener/StockScreener'
import { SuperChartConfiguration_TimeBucket } from '../../../../proto/generated/superchart-page'
import { TimeBucket_TimeSeries } from '../../../../proto/generated/common'
import { DrawingEvents } from '../../../chartV2/drawingTools/drawing.interface'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from '../../../../ErrorFallback'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { Box, Button, Stack, Typography } from '@mui/material'
import { setSecuritySearchOpen } from '../../../../reducer/home.reducer'
import { convertToUTC } from '../../../../util/dateTime'
import { OptionType, Security, SecurityCategory_CategoryCode } from '../../../../proto/generated/security'
import { selectOptionsExpiries, selectOptionsTradeData } from '../../../../selectors/options'
import { fetchOptionChainV2, fetchOptionExpiriesV2 } from '../../../../util/options'
import { selectFuturesExpiryData } from '../../../../selectors/futures'
import { fetchFutureExpiries } from '../../../../util/futures'

export type ChartWidgetSettings = WidgetSettings<{
    chartId: string
    timeBucket?: SuperChartConfiguration_TimeBucket
}>

const ChartWidget = forwardRef<any, WidgetProps<ChartWidgetSettings>>(({ dashboardId, slotId, settings, hoveredSlotId, allowSplit, tradeOptions }, ref) => {
    const { key, expressionMode, securities, timeBucket, displayName } = settings
    const dispatch = useDispatch()
    const chartId = useMemo(() => settings.chartId ?? `${dashboardId}-${slotId}`, [settings.chartId, slotId, dashboardId])
    const appContext = useContext(AppContext)
    const setPrimarySecurity = useSetPrimarySecurity()
    const mounted = useRef(false)
    const removed = useRef(false)
    const isActiveSlot = useSelector(getIsWidgetSlotActiveSelector(dashboardId, slotId))
    const chartBucket = useSelector(selectChartTimeBucket(chartId))
    const timeBuckets = useSelector(selectTimeBuckets)
    const previousSecurityInfo = useRef({ key, expressionMode, securities })
    const previousChartBucket = useRef(chartBucket)
    const comparableSecurities = useSelector(selectChartComparableSecurities(chartId))
    const previousCandleInfo = useRef<{ startTime: number; endTime: number }>(null)
    const [resolvedSecurities, setResolvedSecurities] = useState<Security[]>([])
    const [missingSecurities, setMissingSecurities] = useState<string[]>([])

    const optionExpiries = useSelector(selectOptionsExpiries)
    const optionsTradeData = useSelector(selectOptionsTradeData);
    const futuresExpirues = useSelector(selectFuturesExpiryData);


    const optionExpriesRef = useRef(optionExpiries)
    const optionsTradeDataRef = useRef(optionsTradeData)
    const futuresExpiruesRef = useRef(futuresExpirues)

    useEffect(() => {
        optionExpriesRef.current = optionExpiries
    }, [optionExpiries])

    useEffect(() => {
        optionsTradeDataRef.current = optionsTradeData
    }, [optionsTradeData])

    useEffect(() => {
        futuresExpiruesRef.current = futuresExpirues
    }, [futuresExpirues])

    useEffect(() => {
        if (!securities?.length) {
            setResolvedSecurities([])
            setMissingSecurities([])
            return
        }
        let cancelled = false
        Promise.all(securities.map((k) => getSecurityAsync(k))).then((results) => {
            if (cancelled) return
            setResolvedSecurities(results.filter(Boolean) as Security[])
            setMissingSecurities(securities.filter((_, i) => !results[i]))
        })
        return () => { cancelled = true }
    }, [securities])


    useEffect(() => {
        const mount = securities.length > 0 && resolvedSecurities.length === securities.length && missingSecurities.length === 0

        if (isActiveSlot && mount && chartBucket && !mounted.current) {
            mounted.current = true
            previousChartBucket.current = chartBucket
            deferAfterPaneSelectionPaint(() => {
                dispatch(
                    setActiveChart({
                        chartId,
                        chartFocused: true
                    })
                )
                const { key: currentKey, expressionMode: currentExpressionMode } =
                    store.getState().homeStore.primarySecurity
                if (currentKey !== key || currentExpressionMode !== expressionMode) {
                    setPrimarySecurity(
                        key,
                        resolvedSecurities,
                        expressionMode,
                        displayName
                    )
                }
            })
        }
    }, [key, securities, resolvedSecurities, missingSecurities, displayName, isActiveSlot, slotId, expressionMode, chartId, chartBucket])

    const onMessageRecieved = useCallback(
        (event: MessageEvent<any>) => {
            if (!!sessionStorage.getItem('messageProcessed')) return
            if (event.origin !== window.location.origin) {
                return
            }
            if (event.data?.source === 'parentWindow') {
                const recievedDuration = event?.data?.payload?.timeBucket as TimeDurations
                sessionStorage.setItem('messageProcessed', 'true')
                if (recievedDuration) {
                    let newTimeBucket: SuperChartConfiguration_TimeBucket | undefined
                    if (recievedDuration === '5m') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.MINUTE && t.timeValue === 5)
                    } else if (recievedDuration === '30m') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.MINUTE && t.timeValue === 30)
                    } else if (recievedDuration === '1h') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.HOUR && t.timeValue === 1)
                    } else if (recievedDuration === '1D') {
                        newTimeBucket = timeBuckets.find((t) => t.timeSeries === TimeBucket_TimeSeries.DAY && t.timeValue === 1)
                    }
                    if (newTimeBucket) {
                        const newBucket = {
                            viewKey: chartId,
                            timeBucket: newTimeBucket
                        }
                        dispatch(updateChartTimeBucket(newBucket))
                    }
                }
            }
        },
        [chartId, dispatch, timeBuckets]
    )

    useEffect(() => {
        if (window.opener) {
            window.opener.postMessage({ ready: true, component: 'ChildComponent' }, window.location.origin)
            window.addEventListener('message', onMessageRecieved)
        }
        return () => {
            if (window.opener) {
                window.removeEventListener('message', onMessageRecieved)
            }
        }
    }, [onMessageRecieved])

    useImperativeHandle<any, IWidget>(
        ref,
        () => {
            return {
                onActive() {
                    deferAfterPaneSelectionPaint(() => {
                        dispatch(
                            setActiveChart({
                                chartId,
                                chartFocused: true
                            })
                        )
                        const { key: currentKey, expressionMode: currentExpressionMode } =
                            store.getState().homeStore.primarySecurity
                        if (currentKey !== key || currentExpressionMode !== expressionMode) {
                            setPrimarySecurity(
                                key,
                                resolvedSecurities,
                                expressionMode,
                                displayName
                            )
                        }
                    })
                },
                onBlur() {
                    const chart = appContext.getChart(chartId)
                    chart && chart.onBlur()
                },
                updateSettings(settings: WidgetSettings<any>) {
                    dispatch(
                        updateWidgetSettings({
                            id: dashboardId,
                            slotId,
                            settings
                        })
                    )
                },
                onSyncSettingsChanged(settingKey: keyof DashboardSyncSettings, value: boolean) {
                    switch (settingKey) {
                        case 'security':
                            value &&
                                appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                                    key,
                                    expressionMode,
                                    securities
                                })
                            break
                        case 'timeBucket':
                            value && appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME_BUCKET, previousChartBucket.current)
                    }
                },
                onRemove() {
                    removed.current = true
                },
                onSecurityUpdate: async (data) => {
                    const { key: securityKey, expressionMode: newExpressionMode, securities } = data
                    if (expressionMode || newExpressionMode) {
                        dispatch(
                            updateWidgetSettings({
                                id: dashboardId,
                                slotId,
                                settings: {
                                    key: securityKey,
                                    expressionMode: newExpressionMode,
                                    securities
                                }
                            })
                        )
                    } else {
                        const previousSecurity = await getSecurityAsync(key);
                        const newSecurity = await getSecurityAsync(securityKey);

                        const newUnderlyingSecurityCode = newSecurity?.underlyingSecurityCode || newSecurity?.code;
                        const newUnderlyingSecurityKey = getSecurityKey(newSecurity?.exchange ?? 0, newUnderlyingSecurityCode ?? '')
                        const newUnderlyingSecurity = await getSecurityAsync(newUnderlyingSecurityKey);
                        if ([SecurityCategory_CategoryCode.OPTIONS, SecurityCategory_CategoryCode.FUTURES].includes(previousSecurity?.categoryCode)) {
                            if (previousSecurity?.underlyingSecurityCode !== newUnderlyingSecurityCode) {
                                if (previousSecurity?.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
                                    let trades = optionsTradeDataRef.current?.[newUnderlyingSecurityKey] || [];
                                    let expiries = optionExpriesRef.current?.[newUnderlyingSecurityKey] || [];
                                    
                                    
                                    if (!expiries.length) {
                                        expiries = await fetchOptionExpiriesV2(newUnderlyingSecurity, dispatch);
                                    }
                                    
                                    if (!expiries.length) { // in case there are no options available for the underlying, set the chart to the underlying itself
                                        dispatch(
                                            updateWidgetSettings({
                                                id: dashboardId,
                                                slotId,
                                                settings: {
                                                    key: newUnderlyingSecurityKey,
                                                    expressionMode: false,
                                                    securities: [newUnderlyingSecurityKey]
                                                }
                                            })
                                        )
                                        return;
                                    }
                                    
                                    if (previousSecurity?.derived) {
                                        const derivedExpiry = expiries.find(expiry => expiry.derived);
                                        if (!derivedExpiry || !derivedExpiry.strikes?.[0]) {
                                             dispatch(
                                                updateWidgetSettings({
                                                    id: dashboardId,
                                                    slotId,
                                                    settings: {
                                                        key: newUnderlyingSecurityKey,
                                                        expressionMode: false,
                                                        securities: [newUnderlyingSecurityKey]
                                                    }
                                                })
                                            )
                                            return;
                                        }
                                        const atmSecurity = previousSecurity.optionType === OptionType.CE ? derivedExpiry.strikes[0].callSecurity : derivedExpiry.strikes[0].putSecurity;
                                        const atmSecurityKey = getSecurityKey(atmSecurity.exchange, atmSecurity.code);
                                        dispatch(
                                            updateWidgetSettings({
                                                id: dashboardId,
                                                slotId,
                                                settings: {
                                                    key: atmSecurityKey,
                                                    expressionMode: false,
                                                    securities: [atmSecurityKey]
                                                }
                                            })
                                        )
                                        return;
                                    }
                                    
                                    let initialExpiry = expiries[0].expiry;
                                    if (!trades.some(trade => trade.expiration.expiry?.expiry === initialExpiry.expiry)) {
                                        if (initialExpiry) {
                                            if (!trades.length || !trades.some(trade => trade.expiration.expiry?.expiry === initialExpiry.expiry)) {
                                                trades = (await fetchOptionChainV2({
                                                    security: {
                                                        code: newUnderlyingSecurity.code,
                                                        exchange: newUnderlyingSecurity.exchange
                                                    },
                                                    expiries: [initialExpiry.expiry],
                                                    noOfStrikes: 3,
                                                    requestedFields: [],
                                                    timeBucket: {
                                                        timeSeries: TimeBucket_TimeSeries.DAY,
                                                        value: 1
                                                    },
                                                    sendFutures: false
                                                }, dispatch, trades, "options", false ))?.optionsData || [];
                                            }
                                        }
                                    }

                                    if (trades.length && initialExpiry) {
                                        const currentExpiryTrades = trades.find(trade => trade.expiration.expiry?.expiry === initialExpiry.expiry);
                                        if (currentExpiryTrades) {

                                            const atmStrike = currentExpiryTrades.atmStrike;
                                            const strike = currentExpiryTrades.trades.find(trade => trade.strikeCol.strike === atmStrike);
                                            const atmSecurity = strike.callOption?.security || strike.putOption?.security;
                                            if (!atmSecurity) return;
                                            const atmSecurityKey = getSecurityKey(atmSecurity.exchange, atmSecurity.code);
                                            dispatch(
                                                updateWidgetSettings({
                                                    id: dashboardId,
                                                    slotId,
                                                    settings: {
                                                        key: atmSecurityKey,
                                                        expressionMode: false,
                                                        securities: [atmSecurityKey]
                                                    }
                                                })
                                            )
                                        }
                                        
                                    }
                                } else if (previousSecurity?.categoryCode === SecurityCategory_CategoryCode.FUTURES) {
                                    let expiries = futuresExpiruesRef.current?.[newUnderlyingSecurityKey] || {current: [], past: []};
                                    if (!expiries.current.length) {
                                        const fetchedExpiries = await fetchFutureExpiries(newUnderlyingSecurity);
                                        if (fetchedExpiries?.length) {
                                            expiries.current = fetchedExpiries[0];
                                        } else {
                                            dispatch(
                                                updateWidgetSettings({
                                                    id: dashboardId,
                                                    slotId,
                                                    settings: {
                                                        key: newUnderlyingSecurityKey,
                                                        expressionMode: false,
                                                        securities: [newUnderlyingSecurityKey]
                                                    }
                                                })
                                            )
                                            return;
                                        }
                                    }
                                    
                                    let nearestFuture = expiries.current[0]?.security;

                                    if (previousSecurity?.derived) {
                                        nearestFuture = expiries.current.find(expiry => expiry.derived)?.security || nearestFuture;
                                    }

                                    if (nearestFuture) {    
                                        const nearestFutureKey = getSecurityKey(nearestFuture.exchange, nearestFuture.code);
                                        dispatch(
                                            updateWidgetSettings({
                                                id: dashboardId,
                                                slotId,
                                                settings: {
                                                    key: nearestFutureKey,
                                                    expressionMode: false,
                                                    securities: [nearestFutureKey]
                                                }
                                            })
                                        )
                                    } else {
                                        dispatch(
                                            updateWidgetSettings({
                                                id: dashboardId,
                                                slotId,
                                                settings: {
                                                    key: newUnderlyingSecurityKey,
                                                    expressionMode: false,
                                                    securities: [newUnderlyingSecurityKey]
                                                }
                                            })
                                        )
                                        return;
                                    }
                                }
                            }

                        } else if ([SecurityCategory_CategoryCode.OPTIONS, SecurityCategory_CategoryCode.FUTURES].includes(newSecurity?.categoryCode)) {
                            if (previousSecurity?.underlyingSecurityCode !== newUnderlyingSecurityCode) {
                                dispatch(
                                    updateWidgetSettings({
                                        id: dashboardId,
                                        slotId,
                                        settings: {
                                            key: newUnderlyingSecurityKey,
                                            expressionMode: false,
                                            securities: [newUnderlyingSecurityKey]
                                        }
                                    })
                                )
                            }
                        } else if (previousSecurity?.code !== newSecurity?.code) {
                            dispatch(
                                updateWidgetSettings({
                                    id: dashboardId,
                                    slotId,
                                    settings: {
                                        key: securityKey,
                                        expressionMode: newExpressionMode,
                                        securities
                                    }
                                })
                            )
                        }
                    }
                },
                onTimeBucketUpdate: (timeBucket) => {
                    timeBucket &&
                        dispatch(
                            updateChartTimeBucket({
                                viewKey: chartId,
                                timeBucket
                            })
                        )
                },
                onCrosshairUpdate: ([price, time]) => {
                    const chart = appContext.getChart(chartId)
                    let crossHairTime = time
                    if (!time) {
                        const data = chart.getRenderedPrimaryData()
                        if (data.length) {
                            crossHairTime = data[data.length - 1].time as number
                        }
                    }
                    chart?.setCrossHairPosition(price, crossHairTime)
                },
                onTimeUpdate: ({ startTime, endTime, timeBucket, rangeStart, rangeEnd, barsBefore, barsAfter, gotoDate, middlePointTime }) => {
                    const chart = appContext.getChart(chartId)
                    Number.isFinite(middlePointTime) && chart.goToDate(convertToUTC(middlePointTime!!, chart.timezone))
                    // if (chart.timeBucket.code !== timeBucket.code) {
                    //     Number.isFinite(middlePointTime) && chart.goToDate(convertToUTC(middlePointTime!!, chart.timezone))
                    // } else {
                    //     chart.focusTimeRange(
                    //         {
                    //             startTime,
                    //             endTime,
                    //             rangeStart,
                    //             rangeEnd,
                    //             barsBefore,
                    //             barsAfter,
                    //             viewportCandles: rangeEnd - rangeStart
                    //         },
                    //         timeBucket,
                    //         gotoDate
                    //     )
                    // }
                },
                onDrawingUpdate(eventData) {
                    if (key === eventData.data.securityKey) {
                        const { eventType, data } = eventData
                        const chart = appContext.getChart(chartId)
                        chart?.drawingManager.onDrawingEvent(eventType, data)
                    }
                },
                onDateUpdate: (date) => {
                    const chart = appContext.getChart(chartId)
                    chart?.goToDate(convertToUTC(date, chart?.timezone), true)
                }
            }
        },
        [chartId, key, securities, resolvedSecurities, expressionMode, slotId, isActiveSlot, displayName]
    )

    const onCrosshairMove = useCallback(
        (data?: CrossHairInfo) => {
            if (hoveredSlotId === slotId) {
                appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.CROSSHAIR, [data?.price || 0, data?.time || 0])
            }
        },
        [slotId, hoveredSlotId, appContext.dashboard]
    )

    const onTimeRangeChange = useCallback(
        ({ startTime, endTime, rangeStart, rangeEnd, barsBefore, barsAfter, middlePointTime }: ViewportCandleInfo, gotoDate: number | undefined, initial = false) => {
            if (gotoDate) {
                appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.DATE, gotoDate)
            } else if (Number.isFinite(hoveredSlotId) && hoveredSlotId === slotId) {
                const chart = appContext.getChart(chartId)
                if (!previousCandleInfo.current || previousCandleInfo.current.startTime !== startTime || previousCandleInfo.current.endTime !== endTime) {
                    appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME, {
                        startTime,
                        endTime,
                        rangeStart,
                        rangeEnd,
                        barsBefore,
                        barsAfter,
                        middlePointTime,
                        timeBucket: chart?.chartConfig.timeBucket,
                        gotoDate: gotoDate,
                        viewportCandles: rangeEnd - rangeStart
                    })
                    previousCandleInfo.current = { startTime, endTime }
                }
            }
        },
        [slotId, hoveredSlotId, isActiveSlot, dashboardId]
    )
    useEffect(() => {
        if (!isActiveSlot) return
        if (key && key !== previousSecurityInfo.current.key) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.SECURITY, {
                key,
                expressionMode,
                securities
            })
            previousSecurityInfo.current = {
                key,
                expressionMode,
                securities
            }
        }
    }, [slotId, key, expressionMode, securities, isActiveSlot])

    useEffect(() => {
        if (!previousChartBucket.current || !chartBucket) return

        if (previousChartBucket.current.code !== chartBucket.code) {
            appContext.dashboard?.broadcast(slotId, DashboardSyncEvents.TIME_BUCKET, chartBucket)
            previousChartBucket.current = { ...chartBucket }
        }
    }, [chartBucket])

    useEffect(() => {
        return () => {
            if (removed.current) {
                dispatch(
                    updateChartDrawings({
                        viewKey: chartId,
                        drawings: []
                    })
                )
                dispatch(deleteChartConfig(chartId))
            }
        }
    }, [])

    const onDrawingEvent = (event: DrawingEvents, data: any, forced = false) => {
        if (['add', 'add_point', 'focused', 'update_handle', 'move', 'update_settings', 'remove', 'end', 'update_state', 'sync', 'unsync', 'abort'].indexOf(event) !== -1) {
            appContext.dashboard?.broadcast(
                slotId,
                DashboardSyncEvents.DRAWING,
                {
                    eventType: event,
                    data: { ...data, securityKey: key }
                },
                forced
            )
        }
    }

    if (!chartId) return null

    return missingSecurities.length > 0 ? (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                background: (theme) => theme.palette.background.paper
            }}
        >
            <Stack gap={2}>
                <Typography>Security contains one or more expired contract</Typography>
                <Button
                    variant={'outlined'}
                    onClick={() => {
                        dispatch(
                            setSecuritySearchOpen({
                                open: true,
                                key: 'NSE:NIFTY',
                                isExpression: false
                            })
                        )
                    }}
                >
                    Change Security
                </Button>
            </Stack>
        </Box>
    ) : (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onReset={(reset) => {
                //@ts-ignore
                if (reset?.args?.[0]) {
                    dispatch(
                        updateChartConfig({
                            viewKey: chartId,
                            config: getDefaultChartConfig()
                        })
                    )
                } else {
                    setTimeout(() => {
                        window.location.reload()
                    }, 10)
                }
            }}
        >
            <ChartWrapper
                key={chartId}
                expression={expressionMode ? key : ''}
                expressionName={displayName}
                viewKey={chartId}
                securityList={securities}
                resolvedSecurities={resolvedSecurities}
                mode={expressionMode ? 'EXPRESSION' : 'SINGLE'}
                comparableSecurities={comparableSecurities}
                primary
                hideChartFooter
                onCrosshairMove={onCrosshairMove}
                onDrawingEvent={onDrawingEvent}
                onTimeRangeChange={onTimeRangeChange}
                allowSplit={allowSplit}
                initialConfig={timeBucket ? { timeBucket } : undefined}
                tradeOptions={tradeOptions}
            />
        </ErrorBoundary>
    )
})

ChartWidget.displayName = 'ChartWidget'

export default memo(ChartWidget)
